/**
 * /system/logg-inn — systemadminens innlogging (#178, epic #167 D8).
 *
 * Brukernavn, passord OG koden fra autentiseringsappen (eller en
 * gjenopprettingskode). Står utenfor familieappens skall: ingen navigasjon,
 * ingen familiedata, og ingen kobling til familieinnloggingen på /login.
 *
 * Feil (kontrakt fra system.routes.ts):
 *   - 401 invalid_credentials        → én melding, uansett hvilken faktor
 *   - 401 locked / 429 rate_limited  → nedtelling (usePinLockout)
 *
 * Passordet og koden tømmes etter hvert forsøk. Skjermen er ikke tegnet i
 * design/; den bruker de samme feltene og tokenene som /login.
 */
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import { usePinLockout } from '../../components/hooks/usePinLockout';
import { asApiError, authErrorMessage } from '../auth/authApi';
import { PasswordField, UsernameField } from '../auth/AuthFields';
import { SITE_ADMIN_ME_KEY, SYSTEM_HOME_PATH, SystemApi } from './systemApi';

const LOCKOUT_KEYS = { short: 'auth.error.locked', long: 'auth.error.locked' };

export function SystemLoginRoute(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lockout = usePinLockout(LOCKOUT_KEYS);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locked = lockout.secondsRemaining > 0;
  const canSubmit =
    !pending && !locked && username.trim() !== '' && password !== '' && code.trim() !== '';

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setPending(true);
    setError(null);
    try {
      const res = await SystemApi.login({ username: username.trim(), password, code: code.trim() });
      lockout.cancel();
      qc.setQueryData(SITE_ADMIN_ME_KEY, res.admin);
      navigate(SYSTEM_HOME_PATH, { replace: true });
    } catch (err) {
      const e = asApiError(err);
      if (e.retryAfter) lockout.startLockout(e.retryAfter);
      else
        setError(
          e.code === 'invalid_credentials'
            ? t('system.login.invalid_credentials')
            : authErrorMessage(t, e),
        );
    } finally {
      setPassword('');
      setCode('');
      setPending(false);
    }
  }

  return (
    <main data-route="system-login" className="flex min-h-dvh flex-col">
      <div className="mx-auto flex w-full max-w-md grow flex-col gap-4 px-6 pt-20 pb-9">
        <SectionLabel as="span" flush>
          {t('system.title')}
        </SectionLabel>
        <h1 className="m-0 text-heading-m font-extrabold text-ink">{t('system.login.title')}</h1>
        <form
          data-testid="system-login-form"
          className="flex w-full flex-col gap-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div role="status" aria-live="polite" aria-atomic="true" className="empty:-mb-4">
            {lockout.lockoutMessage ? (
              <p
                data-testid="system-login-lockout"
                className="m-0 flex items-center gap-2.5 rounded-md bg-warn-bg px-3.5 py-3 text-sm font-bold text-ink tnum"
              >
                <Icon.Clock size={20} className="shrink-0 text-warn" />
                {lockout.lockoutMessage}
              </p>
            ) : null}
          </div>
          <UsernameField
            label={t('auth.username')}
            value={username}
            onChange={setUsername}
            testId="system-login-username"
            autoFocus
            validate={false}
          />
          <PasswordField
            label={t('auth.password')}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            testId="system-login-password"
          />
          <div className="flex flex-col gap-1.5">
            <SectionLabel as="label" htmlFor="system-login-code" flush>
              {t('system.login.code')}
            </SectionLabel>
            <input
              id="system-login-code"
              data-testid="system-login-code"
              className="field tnum"
              type="text"
              name="code"
              inputMode="text"
              autoComplete="one-time-code"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={64}
              value={code}
              aria-describedby="system-login-code-hint"
              onChange={(e) => setCode(e.target.value)}
            />
            <span id="system-login-code-hint" className="text-ink-2 text-sm">
              {t('system.login.code_hint')}
            </span>
          </div>
          {error ? (
            <p data-testid="system-login-error" className="m-0 text-sm text-bad" role="alert">
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            size={56}
            disabled={!canSubmit}
            data-testid="system-login-submit"
            className="mt-1 w-full"
          >
            {pending ? t('login.password.submitting') : t('login.password.submit')}
          </Button>
        </form>
      </div>
    </main>
  );
}
